// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useRef, useEffect } from 'react';

import { Popover, Stack } from '@mantine/core';

import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentList, type AttachmentListProps } from 'components/Mantine/Attachments/AttachmentList';
import { ModalOpenGuard, useModalOpenGuardProvider } from 'components/Mantine/Modals/useModalOpenGuard';
import type { BadgeSize } from 'components/Mantine/StatusBadge';
import { BadgeButton } from 'modules/Shop/WorkOrders/WorkOrderDetail/BadgeButton';

type Props = {
  size?: BadgeSize;
  numberOfAttachments: number;
  /**
   * If `true`, will show the badge even if `numberOfAttachments === 0`.
   *
   * @defaultValue false
   */
  showWhenEmpty?: boolean;
} & AttachmentListProps;

export const AttachmentsPopover = ({ numberOfAttachments, size = 'lg', showWhenEmpty, ...props }: Props) => {
  const [opened, setOpened] = useState(false);
  const [keepMounted, setKeepMounted] = useState(false);
  const { hasOpenModal, context: modalOpenContext } = useModalOpenGuardProvider();

  const cachedNumberOfAttachments = useRef(numberOfAttachments);
  useEffect(() => {
    // If the number of attachments changes externally,
    // we need to unmount so that we load the latest attachments when opened.
    if (numberOfAttachments !== cachedNumberOfAttachments.current) {
      cachedNumberOfAttachments.current = numberOfAttachments;
      // If we're already open, it's okay to stay mounted
      setKeepMounted(opened);
    }
  }, [numberOfAttachments, opened]);

  if (!showWhenEmpty && numberOfAttachments === 0) return null;

  return (
    <ModalOpenGuard value={modalOpenContext}>
      <Popover
        withinPortal
        position="left"
        shadow="md"
        width={450}
        withArrow
        opened={opened}
        onOpen={() => setKeepMounted(true)}
        onChange={setOpened}
        keepMounted={keepMounted}
        closeOnClickOutside={!hasOpenModal}
      >
        <Popover.Target>
          <AgGridStyleTooltip label="View and edit attachments" hidden={opened} withArrow>
            <BadgeButton
              icon="Attachment"
              size={size}
              onClick={() => setOpened((o) => !o)}
              className={!opened && showWhenEmpty && numberOfAttachments === 0 ? 'show-on-row-hover' : undefined}
            >
              {numberOfAttachments > 0 ? numberOfAttachments : undefined}
            </BadgeButton>
          </AgGridStyleTooltip>
        </Popover.Target>
        <Popover.Dropdown p={0} onClick={(e) => e.stopPropagation()}>
          <Stack mih={92} mah={400} style={{ overflow: 'hidden' }} pr={2}>
            <Stack style={{ overflowY: 'auto' }} flex={1} p="sm">
              <AttachmentList
                openFullScreenViewer={false}
                {...props}
                onFilesUploaded={(files) => {
                  setOpened(true);
                  props.onFilesUploaded?.(files);
                }}
                onWebLinkAdded={() => {
                  setOpened(true);
                  props.onWebLinkAdded?.();
                }}
                onAttachmentDeleted={() => {
                  setOpened(true);
                  props.onAttachmentDeleted?.();
                }}
              />
            </Stack>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </ModalOpenGuard>
  );
};
